import { type GridApi } from '@ag-grid-community/core';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import TitleWithHelpLink from '@components/_molecules/TitleWithHelpLink';
import ContentController from '@components/_organisms/Headers/ContentController';
import { InfoCardTemplate } from '@components/_templates/InfoCardTemplate';
import { useInfoCard } from '@components/InfoCard';
import { NewComponentCreateRequestTrigger } from '@components/NewComponentCreateRequestTrigger/NewComponentsCreateRequestTrigger';
import { type Component } from '@gql/graphql';
import { useNewComponentRequests } from '@hooks/NewComponentRequests/useNewComponentRequests';
import { type ComponentCreateRequestMapped } from '@pages/notification-components/models/ComponentCreateRequestMapped';
import { ComponentsCard, InfoCard, RequestsCard } from './components';

export default function NotificationComponentsPage(): ReactNode {
    const { setOpen } = useInfoCard();
    const { requests } = useNewComponentRequests();

    const [selectedComponent, setSelectedComponent] =
        useState<Component | null>(null);
    const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
        null,
    );
    const componentsApi = useRef<GridApi<Component> | null>(null);
    const requestsApi = useRef<GridApi<ComponentCreateRequestMapped> | null>(
        null,
    );
    const selectedRequest =
        requests.find((request) => request.id === selectedRequestId) ?? null;

    useEffect(() => {
        setOpen(Boolean(selectedComponent) || Boolean(selectedRequest));
    }, [selectedComponent?.id, selectedRequest?.id, setOpen]);

    function handleRequestChange(request: ComponentCreateRequestMapped): void {
        setSelectedRequestId(request.id);
        if (request) {
            componentsApi.current?.deselectAll();
        }
    }

    function handleComponentChange(component: Component): void {
        setSelectedComponent(component);
        if (component) {
            requestsApi.current?.deselectAll();
        }
    }

    function handleComponentsGridReady(api: GridApi<Component>): void {
        componentsApi.current = api;
    }

    function handleRequestsGridReady(
        api: GridApi<ComponentCreateRequestMapped>,
    ): void {
        requestsApi.current = api;
    }

    return (
        <InfoCardTemplate
            infoCard={
                <InfoCard
                    component={selectedComponent}
                    request={selectedRequest}
                />
            }
        >
            <ContentController
                title={
                    <TitleWithHelpLink
                        label="Notification components"
                        link="https://wiki.siemens-energy.com/x/PW8ZGg"
                    />
                }
            />
            <div className="flex flex-column gap-4 p-4">
                <NewComponentCreateRequestTrigger />
                <RequestsCard
                    requests={requests}
                    selectedChange={handleRequestChange}
                    gridReady={handleRequestsGridReady}
                />
                <ComponentsCard
                    selectedChange={handleComponentChange}
                    gridReady={handleComponentsGridReady}
                />
            </div>
        </InfoCardTemplate>
    );
}
